import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Pressable, ViewStyle, View } from 'react-native'

import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'
import spacings from '@common/styles/spacings'
import { getAvatarColors } from '@common/utils/avatars'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'

import getStyles from './styles'

export type TabType = 'tokens' | 'collectibles' | 'defi' | 'activity'

interface Props {
  openTab: string
  tab: TabType
  tabLabel: string
  setOpenTab: React.Dispatch<React.SetStateAction<TabType>>
  handleChangeQuery: (openTab: TabType) => void
  disabled?: boolean
  testID?: string
  customColors?: [string, string]
  style?: ViewStyle
  children?: ReactNode
}

const Tab = ({
  openTab,
  tab,
  tabLabel,
  setOpenTab,
  handleChangeQuery,
  disabled,
  testID,
  customColors,
  style,
  children
}: Props) => {
  const { t } = useTranslation()
  const { styles, theme } = useTheme(getStyles)
  const { account } = useSelectedAccountControllerState()

  const isActive = openTab === tab

  return (
    <Pressable
      testID={testID}
      onPress={() => {
        if (disabled) return
        handleChangeQuery(tab)
        setOpenTab(tab)
      }}
    >
      {({ hovered }: any) => (
        <View
          style={[
            styles.toggleItem,
            spacings.phLg,
            {
              // Selected state uses the shared design-system active tint
              // (theme.quaternaryBackground), mirroring the mobile-web
              // AssetTypeDropdown selected row instead of a hardcoded grey chip.
              backgroundColor: isActive ? theme.quaternaryBackground : 'transparent',
              opacity: disabled ? 0.4 : 1,
              // @ts-ignore cursor is web only
              cursor: disabled ? 'not-allowed' : 'pointer'
            } as any,
            style
          ]}
        >
          <Text
            weight={isActive ? 'semiBold' : 'medium'}
            color={
              isActive || hovered ? theme.primaryText : theme.secondaryText
            }
            fontSize={14}
          >
            {t(tabLabel)}
          </Text>
          {children}
        </View>
      )}
    </Pressable>
  )
}

export default Tab
